import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { config } from "../config/environment";

interface CompanyInfoData {
  visi_title: string;
  visi_content: string;
  misi_title: string;
  misi_content: string;
}

const CompanyInfo: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<CompanyInfoData>({
    visi_title: "",
    visi_content: "",
    misi_title: "",
    misi_content: "",
  });

  // Fetch company info on component mount
  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${config.api.baseUrl}/company-info`);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch company info");
      }

      if (result.success && result.data) {
        setFormData({
          visi_title: result.data.visi_title || "Visi",
          visi_content: result.data.visi_content || "",
          misi_title: result.data.misi_title || "Misi",
          misi_content: result.data.misi_content || "",
        });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
      console.error("Error fetching company info:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${config.api.baseUrl}/company-info`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to update company info");
      }

      setSuccess("Company info berhasil diupdate!");

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
      console.error("Error updating company info:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading company info...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Edit Visi & Misi Perusahaan
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Update informasi visi dan misi yang akan ditampilkan di halaman About
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {/* Visi Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Visi
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TextField
            fullWidth
            label="Judul Visi"
            name="visi_title"
            value={formData.visi_title}
            onChange={handleInputChange}
            margin="normal"
            required
            placeholder="Contoh: Visi"
          />

          <TextField
            fullWidth
            label="Konten Visi"
            name="visi_content"
            value={formData.visi_content}
            onChange={handleInputChange}
            margin="normal"
            required
            multiline
            rows={4}
            placeholder="Tuliskan visi perusahaan di sini..."
          />

          {/* Misi Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Misi
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TextField
            fullWidth
            label="Judul Misi"
            name="misi_title"
            value={formData.misi_title}
            onChange={handleInputChange}
            margin="normal"
            required
            placeholder="Contoh: Misi"
          />

          <TextField
            fullWidth
            label="Konten Misi"
            name="misi_content"
            value={formData.misi_content}
            onChange={handleInputChange}
            margin="normal"
            required
            multiline
            rows={4}
            placeholder="Tuliskan misi perusahaan di sini..."
          />

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              fullWidth
            >
              {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={fetchCompanyInfo}
              disabled={saving || loading}
              startIcon={<RefreshIcon />}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CompanyInfo;
